import React, { useState } from 'react';
import { 
  Box, Typography, IconButton, Menu, MenuItem, Avatar, Chip
} from '@mui/material';
import { 
  MoreVert, Done, DoneAll, Edit, Delete, Reply, 
  Schedule, Error as ErrorIcon
} from '@mui/icons-material';

const ChatBubble = ({ 
  message, 
  isOwnMessage, 
  showAvatar = true, 
  onEdit, 
  onDelete, 
  onReply 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const getStatusIcon = () => {
    if (message.status === 'sending') return <Schedule sx={{ fontSize: 14 }} />;
    if (message.status === 'failed') return <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />;
    if (message.isSeen) return <DoneAll sx={{ fontSize: 14, color: 'primary.main' }} />;
    return <Done sx={{ fontSize: 14 }} />;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
        marginBottom: 1,
        alignItems: 'flex-end'
      }}
    >
      {!isOwnMessage && showAvatar && (
        <Avatar
          sx={{ width: 32, height: 32, marginRight: 1, marginBottom: 0.5 }}
          src={message.senderAvatar}
        >
          {message.senderName?.[0]?.toUpperCase()}
        </Avatar>
      )}

      <Box
        sx={{
          maxWidth: '70%',
          position: 'relative'
        }}
      >
        {message.replyTo && (
          <Box
            sx={{
              backgroundColor: 'var(--bg-secondary)',
              padding: 1,
              borderRadius: 1,
              marginBottom: 0.5,
              borderLeft: `3px solid ${isOwnMessage ? 'var(--accent-color)' : 'var(--border-color)'}`
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Replying to {message.replyTo.senderName}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {message.replyTo.message}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: isOwnMessage ? 'primary.main' : 'var(--bg-tertiary)',
            color: isOwnMessage ? 'white' : 'var(--text-primary)',
            padding: '8px 12px',
            borderRadius: 2,
            borderTopRightRadius: isOwnMessage ? 1 : 2,
            borderTopLeftRadius: isOwnMessage ? 2 : 1,
            position: 'relative',
            '&:hover .message-menu': {
              opacity: 1
            }
          }}
        >
          {!isOwnMessage && (
            <Typography variant="caption" sx={{ color: 'var(--accent-color)', fontWeight: 600 }}>
              {message.senderName}
            </Typography>
          )}

          {message.isDeleted ? (
            <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.7 }}>
              This message was deleted
            </Typography>
          ) : (
            <>
              {message.type === 'image' && message.imageUrl && (
                <Box sx={{ marginBottom: 1 }}>
                  <img
                    src={message.imageUrl}
                    alt="Shared"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      borderRadius: 8,
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              )}
              
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {message.message}
              </Typography>

              {message.editedAt && (
                <Chip
                  label="edited"
                  size="small"
                  sx={{ 
                    height: 16, 
                    fontSize: 10, 
                    marginTop: 0.5,
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  }}
                />
              )}
            </>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginTop: 0.5,
              gap: 0.5
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: 11, 
                opacity: 0.8,
                color: isOwnMessage ? 'rgba(255,255,255,0.8)' : 'var(--text-tertiary)'
              }}
            >
              {formatTime(message.timestamp)}
            </Typography>
            
            {isOwnMessage && (
              <Box sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {getStatusIcon()}
              </Box>
            )}
          </Box>

          <IconButton
            className="message-menu"
            size="small"
            onClick={handleMenuClick}
            sx={{
              position: 'absolute',
              top: -8,
              right: isOwnMessage ? -8 : 'auto',
              left: isOwnMessage ? 'auto' : -8,
              opacity: 0,
              transition: 'opacity 0.2s',
              backgroundColor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                backgroundColor: 'background.paper'
              }
            }}
          >
            <MoreVert sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { onReply?.(message); handleMenuClose(); }}>
          <Reply sx={{ marginRight: 1, fontSize: 18 }} />
          Reply
        </MenuItem>
        {isOwnMessage && !message.isDeleted && (
          <MenuItem onClick={() => { onEdit?.(message); handleMenuClose(); }}>
            <Edit sx={{ marginRight: 1, fontSize: 18 }} />
            Edit
          </MenuItem>
        )}
        {isOwnMessage && (
          <MenuItem onClick={() => { onDelete?.(message.id); handleMenuClose(); }}>
            <Delete sx={{ marginRight: 1, fontSize: 18 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default ChatBubble;
